import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '@/lib/auth/store';
import { TtsFlowComposeWizard } from '../components/TtsFlowComposeWizard';
import { apiErrorMessage, useCreateTtsFlow, useTtsFlowDetail } from '../api';
import {
  buildTtsFlowRequest,
  hydrateTtsFlowFromDetail,
} from '../payload';
import { synthesizePendingTextRows } from '../synthesize';
import {
  DEFAULT_TTS_FORM_VALUES,
  type TtsFlowFormValues,
} from '../types';
import { commitDraftPrompts } from '../lib/draft-prompts';

export function CloneTtsFlowPage() {
  const navigate = useNavigate();
  const userId = useAuthStore((s) => s.userId);
  const { ttsFlowId } = useParams<{ ttsFlowId: string }>();
  const id = Number(ttsFlowId);

  const detail = useTtsFlowDetail(id);
  const create = useCreateTtsFlow();
  const [synthesizing, setSynthesizing] = useState(false);

  const initial = useMemo<TtsFlowFormValues | null>(() => {
    if (!detail.data) return null;
    const base = hydrateTtsFlowFromDetail(detail.data);
    return {
      ...DEFAULT_TTS_FORM_VALUES,
      ...base,
      // Force a fresh name; clear schedule so the clone doesn't immediately
      // launch on the original's window.
      ttsFlowName: base.ttsFlowName ? `${base.ttsFlowName} (copy)` : '',
      startDate: '',
      endDate: '',
      startTime: '',
      endTime: '',
    };
  }, [detail.data]);

  if (detail.isLoading || !initial) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading TTS flow…
      </div>
    );
  }

  const handleSubmit = async (values: TtsFlowFormValues) => {
    setSynthesizing(true);
    try {
      const rows = await synthesizePendingTextRows({
        userId: Number(userId),
        values,
      });
      const finalValues = { ...values, ttsRows: rows };
      create.mutate(
        buildTtsFlowRequest({ userId: Number(userId), values: finalValues }),
        {
          onSuccess: (res) => {
            if (res?.message === 'Parameters Incorrect') {
              toast.error(res.message);
              return;
            }
             commitDraftPrompts(finalValues.ttsRows);
             toast.success(res?.message ?? 'TTS flow cloned');
            navigate('/tts-flow-list');
          },
          onError: (e) => toast.error(apiErrorMessage(e, 'Could not clone the TTS flow.')),
        },
      );
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : 'Could not synthesize inline text rows.',
      );
    } finally {
      setSynthesizing(false);
    }
  };

  return (
    <TtsFlowComposeWizard
      mode="clone"
      initialValues={initial}
      isPending={create.isPending || synthesizing}
      onSubmit={handleSubmit}
    />
  );
}
